import { c as _c } from "react/compiler-runtime";
import type { CartItemOutputDto, ItemPriceResultDto, ProductPublicDto } from "@sticker-giant/sg-frontend-common";
import { keepPreviousData, queryOptions, useQuery, useQueryClient } from "@tanstack/react-query";
import { buildAllQuantitiesPricingRequest, buildCustomQuantityPricingRequest } from "@/lib/product/pricing";
import { useServicesStore } from "@/stores/useServicesStore";
import { useProduct } from "./useProduct";
function getItemOverrides(item: CartItemOutputDto) {
  return {
    material: item.sku.material,
    finish: item.sku.finish,
    width: item.width,
    height: item.height,
    orientation: item.sku.orientation
  };
}
export function useCartItemPricing(item: CartItemOutputDto, quantities: number[]) {
  const {
    pricingClientPublic
  } = useServicesStore.getState();
  const product = useProduct(item.sku.marketingName)?.data;
  const overrides = getItemOverrides(item);

  // Pricing request in the cart is used only for getting roll quantity data, not price. So customerSkus are not needed
  const request = product && quantities.length > 0 ? buildAllQuantitiesPricingRequest({
    product,
    quantities,
    overrides
  }) : undefined;
  return useQuery<ItemPriceResultDto | undefined>({
    queryKey: ["cart-item-pricing", item.id, item.sku.marketingName, item.sku.material, item.sku.finish, item.width, item.height, item.sku.orientation, ...quantities],
    enabled: Boolean(pricingClientPublic) && Boolean(request) && !!quantities?.length,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      if (!pricingClientPublic || !request) {
        throw new Error("Pricing client is not available or pricing request is missing.");
      }
      return await pricingClientPublic.getPricingForItem(request);
    }
  });
}
function customPricingQueryOptions(item: CartItemOutputDto, quantity: number, product?: ProductPublicDto) {
  const {
    pricingClientPublic
  } = useServicesStore.getState();
  const overrides = getItemOverrides(item);
  return queryOptions<ItemPriceResultDto | undefined>({
    queryKey: ["cart-item-custom-pricing", item.id, item.sku.marketingName, quantity, overrides.material, overrides.finish, overrides.width, overrides.height, overrides.orientation],
    queryFn: async () => {
      if (!pricingClientPublic || !product) {
        throw new Error("Pricing client or product is not available.");
      }
      const request = buildCustomQuantityPricingRequest({
        product,
        customQuantity: quantity,
        overrides
      });
      return await pricingClientPublic.getPricingForItem(request);
    }
  });
}
export function useCartItemPricingCustom(item: CartItemOutputDto, presetQuantities: number[]) {
  const product = useProduct(item.sku.marketingName)?.data;
  const {
    pricingClientPublic
  } = useServicesStore.getState();
  const isCustom = !presetQuantities.includes(item.quantity);
  return useQuery({
    ...customPricingQueryOptions(item, item.quantity, product),
    enabled: isCustom && Boolean(pricingClientPublic) && Boolean(product),
    placeholderData: keepPreviousData
  });
}

/** Fetches (or reads from cache) pricing for a custom quantity the item is about to change to. */
export function useFetchCartItemCustomPricing(item) {
  const $ = _c(4);
  const queryClient = useQueryClient();
  const product = useProduct(item.sku.marketingName)?.data;
  let t0;
  if ($[0] !== item || $[1] !== product || $[2] !== queryClient) {
    t0 = quantity => queryClient.fetchQuery(customPricingQueryOptions(item, quantity, product));
    $[0] = item;
    $[1] = product;
    $[2] = queryClient;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  return t0;
}